import { useTranslation } from "react-i18next";
import { ErrorResponse, useCustomToast } from "features/auth/hooks/use-custom-toast/use-custom-toast";
import { useToast } from "hooks/use-toast";
import { useConfig } from "hooks/use-config";
import { useGlobalMutation, useGlobalQuery } from "state/query-client/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { contactSales, createPayment, getSubscriptionPlans, makeSubscription, cancelSubscription, getCurrentSubscription, mapCurrentSubscription, changeSubscriptionPlan, ChangeSubscriptionPlanRequest } from "../services/plan-subscription-service";
import { useAuthStore } from "state/store/auth/useAuthStore";
import { useCommonStore } from "state/store/common/useCommonStore";
import { useSubscriptionStore } from "state/store/subscription";
import {
    previewQuantityChange,
    updateQuantity,
    getBillingProfile,
    updateBillingProfile,
    previewSubscriptionDiscount,
    type PreviewQuantityChangeRequest,
    type PreviewQuantityChangeResponse,
    type UpdateQuantityRequest,
    type UpdateQuantityResponse,
    type UpdateBillingProfileRequest,
    type UpdateBillingProfileResponse,
    type BillingProfile,
    type DiscountPreviewRequest,
    type DiscountPreviewResult,
} from "features/subscription/services/subscription-service";
import { assignSeats, requestSeatSync } from "features/subscription/services/seat-service";
import { queuePendingSeats } from "features/subscription/utils/pending-seats";


export const useContactSales = () => {
    const { showErrorToast } = useCustomToast();
    const { toast } = useToast();
    const { t } = useTranslation();
    const { configurationSettings } = useConfig();


    interface ContactFormData {
        language: string;
        message: string;
        organizationName: string;
        contactName: string;
        phone: string | undefined;
        emailTo: string;
        email: string;
    }

    return useGlobalMutation({
        mutationKey: ['contactSales'],
        mutationFn: (payload: ContactFormData) => contactSales({ ...payload }, configurationSettings),

        onSuccess: () => {
            toast({
                variant: 'success',
                title: t('SUCCESS'),
                description: t('MESSAGE_SENT_SUCCESSFULLY', 'Message Sent Successfully!'),
            });
        },
        onError: ({ error }: ErrorResponse) => {
            showErrorToast(error);
        },
    });
};

export const useSubscribePlan = () => {
    const { showErrorToast } = useCustomToast();
    const { showSuccessToast } = useCustomToast();
    const { configurationSettings } = useConfig();
    const setSubscriptionDetails = useSubscriptionStore((s) => s.setSubscriptionDetails);
    const queryClient = useQueryClient();

    return useGlobalMutation({
        mutationKey: ['plan-subscription'],
        mutationFn: (payload: any) => makeSubscription(payload, configurationSettings),

        onSuccess: async (response: any, payload: any) => {
            const mapped = mapCurrentSubscription(response);
            if (mapped) {
                setSubscriptionDetails({
                    ...mapped,
                    priceId:
                        mapped.priceId ||
                        (typeof payload?.priceId === 'string' ? payload.priceId : undefined),
                    status: mapped.status || 'Incomplete',
                });
            }
            // Every Klax plan is user-wise and Utilities never seats the buyer, so the person who
            // bought takes one of the seats. A subscription waiting for its payment cannot seat
            // anyone yet: the seat is remembered and given when they come back from paying.
            const userId = useCommonStore.getState().user?.id;
            if (mapped?.subscriptionId && userId) {
                const pending = { subscriptionId: mapped.subscriptionId, userIds: [userId], releases: [] };
                if (mapped.checkoutUrl || mapped.status === 'Incomplete') {
                    queuePendingSeats(pending);
                } else {
                    await assignSeats(mapped.subscriptionId, [userId], configurationSettings).then(
                        () => requestSeatSync([userId], configurationSettings),
                        () => queuePendingSeats(pending)
                    );
                    queryClient.invalidateQueries({ queryKey: ['seat-pools'] });
                }
            }
        },
        onError: ({ error }: ErrorResponse) => {

            showErrorToast(error);
        },
    });
};

export const useGetSubscriptionPlans = () => {
    const { configurationSettings } = useConfig();
    const selectedOrgId = useAuthStore((s) => s.selectedOrgId);
    const user = useCommonStore((s) => s.user);
    const organizationId = selectedOrgId || user?.organizationId || '';

    return useGlobalQuery({
        queryKey: ['subscription-plans', organizationId],
        queryFn: () => getSubscriptionPlans(organizationId, configurationSettings),
        enabled: !!organizationId,
    });
};

export const useCancelSubscription = () => {
    const { showErrorToast } = useCustomToast();
    const { configurationSettings } = useConfig();
    const setSubscriptionDetails = useSubscriptionStore((s) => s.setSubscriptionDetails);

    return useGlobalMutation({
        mutationKey: ['cancel-subscription'],
        mutationFn: (subscriptionId: string) => cancelSubscription(subscriptionId, configurationSettings),
        onSuccess: () => {
            setSubscriptionDetails(null);
        },
        onError: ({ error }: ErrorResponse) => {
            showErrorToast(error);
        },
    });
};

export const useChangeSubscriptionPlan = () => {
    const { showErrorToast } = useCustomToast();
    const { showSuccessToast } = useCustomToast();
    const { t } = useTranslation();
    const { configurationSettings } = useConfig();
    const queryClient = useQueryClient();

    return useGlobalMutation({
        mutationKey: ['plan-subscription'],
        mutationFn: (payload: ChangeSubscriptionPlanRequest) =>
            changeSubscriptionPlan(payload, configurationSettings),
        onSuccess: async (response: any, payload: ChangeSubscriptionPlanRequest) => {
            const persistDetails = (mapped: ReturnType<typeof mapCurrentSubscription>) => {
                const existing = useSubscriptionStore.getState().subscriptionDetails;
                const next = mapped
                    ? {
                        ...existing,
                        ...mapped,
                        priceId: mapped.priceId ?? payload.priceId ?? existing?.priceId,
                        interval: mapped.interval ?? payload.interval ?? existing?.interval,
                        planCode: mapped.planCode || payload.planCode || existing?.planCode || '',
                    }
                    : existing
                        ? {
                            ...existing,
                            planCode: payload.planCode || existing.planCode,
                            priceId: payload.priceId ?? existing.priceId,
                            interval: payload.interval ?? existing.interval,
                        }
                        : {
                            subscriptionId: payload.subscriptionId,
                            planCode: payload.planCode,
                            planName: payload.planCode,
                            priceId: payload.priceId,
                            interval: payload.interval,
                        };

                useSubscriptionStore.getState().setSubscriptionDetails(next);
            };

            try {
                const current = await getCurrentSubscription(configurationSettings);
                persistDetails(mapCurrentSubscription(current));
                queryClient.setQueryData(['current-subscription'], current);
            } catch {
                persistDetails(mapCurrentSubscription(response));
            }

            showSuccessToast({
                title: t('SUCCESS'),
                description: t('PLAN_UPDATED', 'Plan updated'),
            });
        },
        onError: ({ error }: ErrorResponse) => {
            showErrorToast(error);
        },
    });
};

export const useGetCurrentSubscription = () => {
    const { configurationSettings } = useConfig();
    const setSubscriptionDetails = useSubscriptionStore((s) => s.setSubscriptionDetails);

    return useGlobalQuery({
        queryKey: ['current-subscription'],
        queryFn: async () => {
            const res = await getCurrentSubscription(configurationSettings);
            const mapped = mapCurrentSubscription(res);
            if (mapped) {
                setSubscriptionDetails(mapped);
            }
            return res;
        },
        enabled: !!configurationSettings,
        refetchOnWindowFocus: true,
        refetchOnMount: true,
    });
};

/**
 * Custom hook to preview subscription quantity changes (Team plan seats).
 *
 * @param {string} subscriptionId - The subscription ID to preview changes for.
 * @param {Object} options - Optional configuration for mutation.
 * @param {Function} options.onSuccess - Callback function called when preview is successful.
 * @param {Function} options.onError - Callback function called when preview fails.
 * @returns {Object} The mutation result for previewing quantity change.
 *
 * @example
 * const { mutate } = usePreviewQuantityChange('sub-123', {
 *   onSuccess: (response) => {
 *     console.log('Prorated charge:', response.proratedChargeMinor / 100);
 *   }
 * });
 */
export function usePreviewQuantityChange(
    subscriptionId: string,
    options?: {
        onSuccess?: (data: PreviewQuantityChangeResponse) => void;
        onError?: (error: unknown) => void;
    }
) {
    const { configurationSettings } = useConfig();
    const { toast } = useToast();
    const { t } = useTranslation();

    return useGlobalMutation({
        mutationFn: (data: PreviewQuantityChangeRequest) =>
            previewQuantityChange(subscriptionId, data, configurationSettings),
        onSuccess: (data) => {
            options?.onSuccess?.(data);
        },
        onError: (error) => {
            toast({
                title: t('ERROR'),
                description: t('FAILED_PREVIEW_QUANTITY_CHANGE') || 'Failed to preview quantity change',
                variant: 'destructive',
            });
            options?.onError?.(error);
        },
    });
}

/**
 * Custom hook to update subscription quantity (Team plan seats).
 *
 * @param {string} subscriptionId - The subscription ID to update.
 * @param {Object} options - Optional configuration for mutation.
 * @param {Function} options.onSuccess - Callback function called when update is successful.
 * @param {Function} options.onError - Callback function called when update fails.
 * @returns {Object} The mutation result for updating quantity.
 *
 * @example
 * const { mutate } = useUpdateQuantity('sub-123', {
 *   onSuccess: (response) => {
 *     console.log('Seats updated successfully');
 *   }
 * });
 */
export function useUpdateQuantity(
    subscriptionId: string,
    options?: {
        onSuccess?: (data: UpdateQuantityResponse) => void;
        onError?: (error: unknown) => void;
    }
) {
    const { configurationSettings } = useConfig();
    const { toast } = useToast();
    const { t } = useTranslation();
    const queryClient = useQueryClient();

    return useGlobalMutation({
        mutationFn: (data: UpdateQuantityRequest) =>
            updateQuantity(subscriptionId, data, configurationSettings),
        onSuccess: (data) => {
            const { subscriptionDetails, currentPlan, setSubscriptionDetails, setCurrentPlan } =
                useSubscriptionStore.getState();
            const existing = subscriptionDetails ?? currentPlan;
            if (existing && existing.subscriptionId === subscriptionId) {
                const next = {
                    ...existing,
                    quantities: data.quantities ?? existing.quantities,
                    version: data.version ?? existing.version,
                    currencyCode: data.currencyCode ?? existing.currencyCode,
                    pendingQuantityChange:
                        data.pendingQuantityChange !== undefined
                            ? data.pendingQuantityChange
                            : existing.pendingQuantityChange,
                    taxAmountMinor: data.taxAmountMinor ?? existing.taxAmountMinor,
                    netAmountMinor: data.netAmountMinor ?? existing.netAmountMinor,
                    grossAmountMinor: data.grossAmountMinor ?? existing.grossAmountMinor,
                };
                setSubscriptionDetails(next);
                setCurrentPlan(next);
            }
            queryClient.invalidateQueries({ queryKey: ['current-subscription'] });
            queryClient.invalidateQueries({ queryKey: ['seat-pools'] });
            toast({
                variant: 'success',
                title: t('SUCCESS'),
                description: t('SEATS_UPDATED_SUCCESSFULLY', 'Seats updated successfully'),
            });
            options?.onSuccess?.(data);
        },
        onError: (error) => {
            toast({
                title: t('ERROR'),
                description: t('FAILED_UPDATE_QUANTITY') || 'Failed to update seats',
                variant: 'destructive',
            });
            options?.onError?.(error);
        },
    });
}

export function useGetBillingProfile() {
    const { configurationSettings } = useConfig();
    const selectedOrgId = useAuthStore((s) => s.selectedOrgId);
    const user = useCommonStore((s) => s.user);
    const organizationId = selectedOrgId || user?.organizationId || '';

    return useGlobalQuery<BillingProfile | null>({
        queryKey: ['subscription-billing-profile', organizationId],
        queryFn: () => getBillingProfile(configurationSettings),
        enabled: !!configurationSettings,
    });
}

export function useUpdateBillingProfile(options?: {
    onSuccess?: (data: UpdateBillingProfileResponse) => void;
    onError?: (error: unknown) => void;
}) {
    const { configurationSettings } = useConfig();
    const { showErrorToast } = useCustomToast();
    const queryClient = useQueryClient();

    return useGlobalMutation({
        mutationKey: ['update-billing-profile'],
        mutationFn: (data: UpdateBillingProfileRequest) =>
            updateBillingProfile(data, configurationSettings),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['subscription-billing-profile'] });
            options?.onSuccess?.(data);
        },
        onError: (error: ErrorResponse) => {
            showErrorToast(error);
            options?.onError?.(error);
        },
    });
}

/**
 * Custom hook to preview a discount/coupon code against the current plan quote.
 * Does not redeem the code — safe to call repeatedly while a user is typing a coupon.
 *
 * @param {Object} options - Optional configuration for the mutation.
 * @param {Function} options.onSuccess - Called with the discount preview result (check `status` for validity).
 * @param {Function} options.onError - Called when the request itself fails (network/server error).
 * @returns {Object} The mutation result for previewing a discount code.
 */
export function usePreviewSubscriptionDiscount(options?: {
    onSuccess?: (data: DiscountPreviewResult) => void;
    onError?: (error: unknown) => void;
}) {
    const { configurationSettings } = useConfig();

    return useGlobalMutation({
        mutationKey: ['preview-subscription-discount'],
        mutationFn: (data: DiscountPreviewRequest) =>
            previewSubscriptionDiscount(data, configurationSettings),
        onSuccess: (data) => {
            options?.onSuccess?.(data);
        },
        onError: (error) => {
            options?.onError?.(error);
        },
    });
}